import { apiFetch, isApiMode } from './http';
import { REAUTHENTICATION_REQUIRED } from './reauthenticationSignal';

// ─────────────────────────────────────────────────────────────────────
// THE ORGANIZATION'S OWN «SAMARBEID» PAGE — the client (slice 4a, 2026-10-20).
//
// `/organization/links` is tenant-scoped: the organization is the one the
// portal is acting in (the tenant header `apiFetch` sets), never a parameter.
// The other party is named — a club by its slug from the public directory, a
// link by its id. Everything here is a VIEW of what the server decided; the
// owner's rules (ROLES_AND_ACCESS.md §4h, decision 5) are the server's, each a
// code here:
//
//   only_a_centre_proposes   a club answers proposals; it does not make one
//   only_the_club_accepts    the centre cannot say yes on the club's behalf
//   not_proposed             the proposal was already answered
//   not_accepted             the club gives when it says yes, not before
//   terms_are_the_centres    the agreed price is the centre's to set
//   link_exists              the two already have a link (or a proposal)
// ─────────────────────────────────────────────────────────────────────

/**
 * The capability every verb on `/organization/links` asks for.
 *
 * The server's route exports the same literal
 * (`apps/api/src/routes/organizationLinks.ts`), and
 * `organization-links-gate.test.tsx` reads that file to keep the two spellings
 * identical. The browser uses it for ONE thing: deciding whether to DRAW the
 * «Samarbeid» entry in the organization's settings.
 */
export const ORGANIZATION_LINKS_MANAGE = 'organization.links.manage';

export interface LinkRoleRef {
  id: string;
  key: string;
  /** The role's name as /sa shows it — Norwegian, from the row. */
  name: string;
}

export interface LinkRoleOption extends LinkRoleRef {
  description: string | null;
  isSystem: boolean;
  /** The `link.*` capabilities it carries — what choosing it gives the other side. */
  capabilities: string[];
}

export type LinkStatus = 'proposed' | 'active' | 'suspended';

export interface OrganizationLinkView {
  id: string;
  status: LinkStatus;
  /** Which side of the link THIS organization is on. */
  side: 'centre' | 'club';
  other: { id: string; slug: string; name: string; kind: string };
  /** What this organization gives the other — its own choice. */
  gives: LinkRoleRef | null;
  /** What the other organization gives this one — its choice, read only here. */
  receives: LinkRoleRef | null;
  settlementModel: string;
  /** Øre per hour, or null for the centre's list price. */
  agreedCourtRateOre: number | null;
  /** How far ahead the partner club may book with extended_horizon (slice 4c); null = ordinary. */
  bookingWindowDays: number | null;
  createdAt: string;
  acceptedAt: string | null;
}

export interface OrganizationLinksList {
  organization: { id: string; kind: string };
  links: OrganizationLinkView[];
  linkRoles: LinkRoleOption[];
}

export type LinksListResult =
  | { state: 'no-api' }
  | { state: 'ok'; list: OrganizationLinksList }
  /** No session, or one without `organization.links.manage` here. */
  | { state: 'forbidden' }
  | { state: 'error'; status: number | null };

/** This organization's links and the roles a side may give. Never throws. */
export async function fetchOrganizationLinks(): Promise<LinksListResult> {
  if (!isApiMode()) return { state: 'no-api' };
  try {
    const res = await apiFetch('/organization/links');
    if (res.status === 401 || res.status === 403) return { state: 'forbidden' };
    if (!res.ok) return { state: 'error', status: res.status };
    return { state: 'ok', list: (await res.json()) as OrganizationLinksList };
  } catch {
    return { state: 'error', status: null };
  }
}

export type LinkWriteResult =
  | { state: 'ok' }
  | { state: 'forbidden' }
  /** A machine code from the API — never a sentence; the page translates it. */
  | { state: 'refused'; reason: string }
  | { state: 'error'; status: number | null };

const JSON_HEADERS = { 'Content-Type': 'application/json' };

/**
 * Refusals that are 403s with a NAME — a rule, not a missing right. The
 * second is #1014 decision 7: the writes want a sign-in from the last fifteen
 * minutes. The transport has already raised the step-up prompt; the page says
 * why the change did not happen instead of telling an Eier they are not one.
 */
const NAMED_403 = new Set(['terms_are_the_centres', REAUTHENTICATION_REQUIRED]);

async function write(path: string, init: RequestInit): Promise<LinkWriteResult> {
  if (!isApiMode()) return { state: 'error', status: null };
  try {
    const res = await apiFetch(path, init);
    if (res.ok) return { state: 'ok' };
    const body = (await res.json().catch(() => null)) as Record<string, unknown> | null;
    const reason = typeof body?.error === 'string' ? body.error : null;
    if (res.status === 403 && reason && NAMED_403.has(reason)) return { state: 'refused', reason };
    if (res.status === 401 || res.status === 403) return { state: 'forbidden' };
    if (res.status === 400 || res.status === 404 || res.status === 409) {
      return { state: 'refused', reason: reason ?? 'refused' };
    }
    return { state: 'error', status: res.status };
  } catch {
    return { state: 'error', status: null };
  }
}

/** The CENTRE proposes a co-operation to a club and says what the club gets. */
export function proposeOrganizationLink(input: {
  clubSlug: string;
  gives: string | null;
  agreedCourtRateOre?: number | null;
}): Promise<LinkWriteResult> {
  return write('/organization/links', {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify(input),
  });
}

/** The CLUB says yes, and says what the centre gets. */
export function acceptOrganizationLink(id: string, gives: string | null): Promise<LinkWriteResult> {
  return write(`/organization/links/${encodeURIComponent(id)}/accept`, {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify({ gives }),
  });
}

/** Change what THIS side gives — and, for the centre, the agreed price. */
export function updateOrganizationLink(
  id: string,
  change: {
    gives?: string | null;
    agreedCourtRateOre?: number | null;
    bookingWindowDays?: number | null;
  },
): Promise<LinkWriteResult> {
  return write(`/organization/links/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: JSON_HEADERS,
    body: JSON.stringify(change),
  });
}

/** End a link — or withdraw (centre) or decline (club) a proposal. */
export function endOrganizationLink(id: string): Promise<LinkWriteResult> {
  return write(`/organization/links/${encodeURIComponent(id)}`, { method: 'DELETE' });
}
